import { useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";

import { getSessionToken } from "@/common/auth";
import { logoutGatewaySession } from "@/common/gateway-session";
import { Button } from "@/components/ui/Button";
import { useUser } from "@/hooks/useUser";
import { api } from "@/trpc/react";

// Right after signup a session is valid but the account row is still being
// mirrored (the account.created / user.created webhook). acceptInvitation
// reports that as TIMEOUT (a real "provisioning" signal from the backend, as
// opposed to UNAUTHORIZED = genuinely signed out), so we retry on that signal
// until the account lands — without guessing, and without delaying a signed-out
// visitor's path to signup.
const MAX_PROVISIONING_RETRIES = 4;
const PROVISIONING_RETRY_DELAY_MS = 1500;

const inviteAcceptSearchSchema = z.object({
  token: z.string().optional(),
});

export const Route = createFileRoute("/_rootLayout/invite/accept")({
  validateSearch: zodValidator(inviteAcceptSearchSchema),
  ssr: false,
  component: InviteAcceptRoute,
});

function InviteAcceptRoute() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  // isLoaded = auth READINESS only (so the tRPC bearer is attached before the
  // first request). We deliberately do NOT read isSignedIn anywhere in the accept
  // path: in gateway-exchange mode it's identity-based and can be false while a
  // valid session token is still present. Whether the request is authenticated,
  // and whether an authenticated account is still provisioning, is decided
  // authoritatively by the server via the acceptInvitation error code
  // (UNAUTHORIZED vs TIMEOUT vs FORBIDDEN) — see onError below.
  const { isLoaded } = useUser();

  const preview = api.organization.previewInvitation.useQuery(
    { token: token ?? "" },
    { enabled: Boolean(token), retry: false },
  );

  const provisioningRetries = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasFired = useRef(false);
  // Monotonic guard for async continuations (token refreshes, scheduled
  // retries): bumped on unmount / invite-token change and checked before any
  // deferred accept.mutate, so a refresh that was already in flight when the
  // user navigated away can't launch a stale acceptance (and assign its seat)
  // afterwards. clearTimeout alone can't cover that window.
  const retryEpoch = useRef(0);
  // One fresh-token re-probe per invite before trusting an UNAUTHORIZED: a
  // transient /session/token failure makes the client send "Bearer null" even
  // though a valid cookie session exists, and that must not read as signed-out.
  const unauthorizedRetried = useRef(false);
  // Complement of the epoch guard: TanStack runs useMutation callbacks even
  // after unmount, so a callback that BEGINS after cleanup would sample the
  // already-bumped epoch and pass the guard. isActive is flipped false in the
  // same cleanup and checked at the top of onSuccess/onError, so nothing gets
  // scheduled after cleanup, and the epoch covers what was already in flight.
  const isActive = useRef(true);
  const [provisioningExhausted, setProvisioningExhausted] = useState(false);

  const acceptPath = `/invite/accept?token=${encodeURIComponent(token ?? "")}`;

  const goToSignup = () =>
    void navigate({
      to: "/signup",
      search: { redirect_url: acceptPath, invite: token },
    });

  // Cancel a scheduled retry if the component unmounts or the token changes, so
  // a pending timer can't fire and accept a stale invitation (assigning its
  // seat) after the user has navigated away or opened a different invite.
  useEffect(() => {
    provisioningRetries.current = 0;
    hasFired.current = false;
    unauthorizedRetried.current = false;
    isActive.current = true;
    return () => {
      isActive.current = false;
      retryEpoch.current += 1;
      if (retryTimer.current) clearTimeout(retryTimer.current);
    };
  }, [token]);

  const accept = api.organization.acceptInvitation.useMutation({
    onSuccess: (result, variables) => {
      // If the user already navigated away, the membership is created but we
      // don't yank them back with a hard redirect. The variables check pins the
      // callback to ITS OWN invite generation: isActive alone flips back true
      // when a different token mounts, which must not let invite A's late
      // success redirect the user to A's organization from B's page.
      if (!isActive.current || variables.token !== token) return;
      // Members land on the read-only org list; admins on the manage page.
      // Hard nav so the freshly-joined org + session are re-read cleanly.
      window.location.assign(
        result.role === "admin"
          ? `/organizations/${result.slug}`
          : "/account/organizations",
      );
    },
    onError: (error, variables) => {
      // A callback that begins after cleanup must not schedule ANY follow-up
      // (retry timer, token re-probe, navigation) for an abandoned invite, and
      // a callback from a PREVIOUS invite generation (variables.token mismatch)
      // must not act on the current one.
      if (!isActive.current || variables.token !== token) return;
      const code = error.data?.code;
      // UNAUTHORIZED from the server means the REQUEST carried no valid session
      // (TIMEOUT covers authenticated-but-provisioning in both auth modes -- see
      // accountPending in trpc.ts). But the request can be tokenless while a
      // valid session still exists client-side: a transient /session/token
      // failure sends "Bearer null". So before treating it as signed-out, force
      // one fresh token fetch -- if that yields a token, re-fire the accept with
      // it; only a null re-probe (genuinely no session) goes to signup.
      if (code === "UNAUTHORIZED") {
        if (!unauthorizedRetried.current) {
          unauthorizedRetried.current = true;
          const epoch = retryEpoch.current;
          getSessionToken({ skipCache: true })
            .then((fresh) => {
              if (retryEpoch.current !== epoch) return;
              if (fresh) accept.mutate({ token: token ?? "" });
              else goToSignup();
            })
            .catch(() => {
              if (retryEpoch.current === epoch) goToSignup();
            });
          return;
        }
        goToSignup();
        return;
      }
      // TIMEOUT = account still provisioning; retry on a short fixed delay.
      // Everything else (FORBIDDEN wrong-email, etc.) surfaces in the render.
      if (code !== "TIMEOUT") return;
      if (provisioningRetries.current < MAX_PROVISIONING_RETRIES) {
        provisioningRetries.current += 1;
        retryTimer.current = setTimeout(() => {
          // Re-prime the token before re-firing. skipCache only bypasses the
          // BROWSER cache - /session/token returns the same access token
          // while it is still fresh, so the retry usually resends the same
          // bearer, and that is fine: the API re-reads the account row on
          // every request, which is what makes the retry succeed once
          // provisioning lands. The re-prime only matters when the token
          // expired mid-wait; a failed refresh just falls through to the
          // retry. The epoch check covers the refresh's in-flight window,
          // where clearTimeout can no longer stop a stale acceptance.
          const epoch = retryEpoch.current;
          void getSessionToken({ skipCache: true }).finally(() => {
            if (retryEpoch.current !== epoch) return;
            accept.mutate({ token: token ?? "" });
          });
        }, PROVISIONING_RETRY_DELAY_MS);
      } else {
        setProvisioningExhausted(true);
      }
    },
  });

  // The /signout ROUTE only redirects and leaves the session intact. Do a
  // REAL logout through logoutGatewaySession (POST /auth/logout chain).
  // (Known asymmetry: the logout chain lands on the product root, so this
  // path drops the invite token and the user re-clicks the email link;
  // acceptable for the rare wrong-account case until the logout chain grows a
  // return-to param.)
  const useDifferentAccount = () => {
    void logoutGatewaySession();
  };

  const inviteReady =
    Boolean(token) && Boolean(preview.data) && !preview.data?.expired;

  // Auto-advance once auth has loaded: fire the accept straight away. We do NOT
  // branch on a client "signed in" flag — in gateway-exchange mode it's derived
  // from a userinfo query and reads false while a session token is still present
  // (userinfo loading/error, and the post-signup account-mirror race). Gating on
  // it would bounce genuinely-signed-in invitees to /signup. Instead the server
  // is authoritative: acceptInvitation returns UNAUTHORIZED for a truly
  // token-less request, and onError routes that to signup (carrying the token,
  // which prefills the invited email). FORBIDDEN (wrong email) and TIMEOUT
  // (provisioning) surface in the render below.
  useEffect(() => {
    if (!inviteReady || !isLoaded || hasFired.current) return;
    hasFired.current = true;
    accept.mutate({ token: token ?? "" });
    // token derives from route search; accept is stable — deps intentionally
    // narrowed to the auth+invite gates so this fires exactly once per invite.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteReady, isLoaded]);

  if (!token) {
    return (
      <InviteCard
        title="Invitation not found"
        body="This invitation link is missing its token. Ask for a new invitation."
      />
    );
  }
  if (preview.isLoading) {
    return <InviteCard title="Loading invitation..." body="One moment." />;
  }
  if (preview.isError || !preview.data) {
    // Only a NOT_FOUND means the token is genuinely invalid/used. A transient
    // network/server error must not be reported as a dead invitation — offer a
    // retry instead (the query disables auto-retry).
    const invalid = preview.error?.data?.code === "NOT_FOUND";
    return (
      <InviteCard
        title={invalid ? "Invitation unavailable" : "Couldn't load invitation"}
        body={
          invalid
            ? "This invitation link is invalid or has already been used. Ask the organization for a new one."
            : "We couldn't load this invitation just now. Check your connection and try again."
        }
      >
        {invalid ? null : (
          <Button
            variant="steward"
            disabled={preview.isFetching}
            onClick={() => void preview.refetch()}
          >
            {preview.isFetching ? "Retrying..." : "Try again"}
          </Button>
        )}
      </InviteCard>
    );
  }

  const { email, organizationName, expired } = preview.data;

  if (expired) {
    return (
      <InviteCard
        title="Invitation expired"
        body={`Your invitation to join ${organizationName} has expired. Ask the organization for a new one.`}
      />
    );
  }

  const errorCode = accept.error?.data?.code;

  // Wrong account: signed in with an email that doesn't match the invite. The
  // one place the "use a different account" escape belongs.
  if (errorCode === "FORBIDDEN") {
    return (
      <InviteCard
        title="Wrong account"
        body={`This invitation is for ${email}, but you're signed in as a different account. Sign out and continue with ${email} to accept.`}
      >
        <Button variant="steward" onClick={useDifferentAccount}>
          Use a different account
        </Button>
      </InviteCard>
    );
  }

  // Provisioning after signup exhausted its retries: offer a manual retry
  // rather than a dead end.
  if (errorCode === "TIMEOUT" && provisioningExhausted) {
    return (
      <InviteCard
        title={`Join ${organizationName}`}
        body="Your account is taking longer than expected to finish setting up."
      >
        <Button
          variant="steward"
          disabled={accept.isPending}
          onClick={() => {
            provisioningRetries.current = 0;
            setProvisioningExhausted(false);
            accept.mutate({ token });
          }}
        >
          Try again
        </Button>
        <SwitchAccountLink onClick={useDifferentAccount} />
      </InviteCard>
    );
  }

  // Any other failure: show it with a retry and the sign-out escape. UNAUTHORIZED
  // is excluded: onError already redirects it to signup, so don't flash an error
  // card during the navigation.
  if (accept.error && errorCode !== "TIMEOUT" && errorCode !== "UNAUTHORIZED") {
    return (
      <InviteCard
        title={`Join ${organizationName}`}
        body={accept.error.message}
      >
        <Button
          variant="steward"
          disabled={accept.isPending}
          onClick={() => accept.mutate({ token })}
        >
          Try again
        </Button>
        <SwitchAccountLink onClick={useDifferentAccount} />
      </InviteCard>
    );
  }

  // Settling: auth resolving, the accept in flight, or a redirect to signup
  // underway (onError → UNAUTHORIZED). The effect above drives the transition.
  return (
    <InviteCard
      title={`Joining ${organizationName}...`}
      body={isLoaded ? "Adding you to the team." : "One moment."}
    />
  );
}

function SwitchAccountLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-xs underline transition-colors duration-200 hover:text-[var(--ink)]"
      style={{ color: "var(--steward-muted)" }}
    >
      Use a different account
    </button>
  );
}

function InviteCard({
  title,
  body,
  children,
}: {
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-5 text-center">
        <h1 className="text-xl font-medium" style={{ color: "var(--ink)" }}>
          {title}
        </h1>
        <p className="text-sm" style={{ color: "var(--steward-muted)" }}>
          {body}
        </p>
        {children}
      </div>
    </div>
  );
}
